import * as React from "react";

import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorMessage, cancelLinkAppointment, cancelPageBooking } from "@/api/client";
import { noteForRequest } from "./inviteeNote";
import { whenText } from "./BookingRows";

/**
 * The host's cancel-a-booking panel, shared by every surface that shows a booked slot: the slot
 * grid on a booking page and the blocks on the meetings calendar.
 *
 * <p>It is a popover rather than a modal because it hangs off the slot the host clicked, which is
 * what makes it obvious which booking is about to go. Radix supplies the focus trap, Esc, the
 * outside-click dismissal and focus restore, and its portal is what lets the panel escape
 * FullCalendar's clipped scroller. The one thing this owns is the two-step shape: a summary, so a
 * stray click cannot cancel anything, and then the note.</p>
 */

export type CancelBookingKind = "adhoc" | "rolling";

export interface CancelBookingTarget {
  kind: CancelBookingKind;
  /** The booking page id, or the rolling link id. The owner scope in the URL. */
  linkId: string;
  bookingId: string;
  /** The invitee's name, already fallen back to their email by the caller. */
  invitee: string;
  start: string;
  end?: string | null;
}

/** Sends the cancel for whichever kind of booking this is. */
function cancel(target: CancelBookingTarget, note?: string) {
  return target.kind === "adhoc"
    ? cancelPageBooking(target.linkId, target.bookingId, note)
    : cancelLinkAppointment(target.linkId, target.bookingId, note);
}

export function CancelBookingPopover({
  target,
  anchor,
  onClose,
  onCancelled,
  onFailed,
  extraAction,
}: {
  /** Null closes the panel. */
  target: CancelBookingTarget | null;
  /** The element to hang off: a slot chip, or a calendar event block. */
  anchor: HTMLElement | null;
  onClose: () => void;
  /** Fired after the server confirms. The surface refreshes; the page raises the toast. */
  onCancelled: (target: CancelBookingTarget) => void;
  /** Fired on failure, so the page can raise a toast even if the panel is dismissed. */
  onFailed?: (message: string) => void;
  /** An extra row above the cancel action, e.g. the calendar's "Open in bookings". */
  extraAction?: React.ReactNode;
}) {
  const [step, setStep] = React.useState<"summary" | "confirm">("summary");
  const [note, setNote] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  // A new target is a different booking, so never inherit the last one's half-typed note or error.
  React.useEffect(() => {
    setStep("summary");
    setNote("");
    setBusy(false);
    setError(null);
  }, [target?.bookingId]);

  const anchorRef = React.useRef<HTMLElement | null>(null);
  anchorRef.current = anchor;

  async function confirm() {
    if (!target || busy) return;
    setBusy(true);
    setError(null);
    try {
      await cancel(target, noteForRequest(note));
      onCancelled(target);
      onClose();
    } catch (e) {
      const message = apiErrorMessage(e);
      setError(message);
      onFailed?.(message);
      setBusy(false);
    }
  }

  return (
    <Popover open={target != null} onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        side="top"
        align="center"
        className="w-64 p-3"
        onOpenAutoFocus={(e) => {
          // The summary step opens with a destructive button under the cursor; landing focus on it
          // would let a stray Enter cancel the booking. The confirm step wants the note instead.
          if (step === "summary") e.preventDefault();
        }}
      >
        {target && (
          <div className="flex flex-col gap-2">
            <div>
              <div className="text-sm font-semibold text-foreground">{target.invitee}</div>
              <div className="text-xs text-muted-foreground">{whenText(target.start)}</div>
            </div>

            {step === "summary" ? (
              <>
                {extraAction}
                <button
                  type="button"
                  onClick={() => setStep("confirm")}
                  className="rounded-full border border-brand-coral/60 px-3 py-1.5 text-xs font-medium text-brand-coral transition hover:bg-brand-coral/10 motion-safe:hover:-translate-y-px"
                >
                  Cancel booking
                </button>
              </>
            ) : (
              <>
                <label
                  htmlFor="cancel-booking-note"
                  className="text-xs font-medium text-muted-foreground"
                >
                  Note to the invitee (optional)
                </label>
                <Textarea
                  id="cancel-booking-note"
                  autoFocus
                  rows={3}
                  maxLength={500}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  className="text-sm"
                />
                {error && <div className="text-xs text-brand-coral">{error}</div>}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={busy}
                    onClick={confirm}
                    className="rounded-full border border-brand-coral/60 px-3 py-1.5 text-xs font-medium text-brand-coral transition hover:bg-brand-coral/10 disabled:opacity-60 motion-safe:hover:-translate-y-px"
                  >
                    {busy ? "Cancelling…" : "Cancel booking"}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={onClose}
                    className="rounded-full border border-border px-3 py-1.5 text-xs text-muted-foreground transition hover:text-foreground disabled:opacity-60"
                  >
                    Keep
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
